import React, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ListProvider } from './context';
import ErrorMessage from './ErrorMessage';
import { ListSelectionTypes } from './helper';
import { ListActions } from './ListActions';
import { ListBottomBar } from './ListBottomBar';
import { ListDataCard } from './ListDataCard';
import { ListItem } from './ListItem';
import { ListItemGroup } from './ListItemGroup';
import { ListMidSection } from './ListMidSection';
import { ListMoreActions } from './ListMoreActions';
import { ListSkeleton } from './ListSkeleton';
import { ListTitleBlock } from './ListTitleBlock';
import { ListTopBar } from './ListTopBar';
import { debounce } from '../Utils/helper';
import moduleStyles from '@dtsl/css-list/dist/index.module.css';
function List(props) {
  const {
    children,
    className = '',
    listSelectionType = ListSelectionTypes.DEFAULT,
    topBarLeftSlot = /*#__PURE__*/React.createElement(React.Fragment, null),
    topBarRightSlot = /*#__PURE__*/React.createElement(React.Fragment, null),
    bottomBarLeftSlot = /*#__PURE__*/React.createElement(React.Fragment, null),
    listSearchInputProps = {},
    paginationMetaData = null,
    topBarPaginationMetaData = null,
    bulkActionMetaData = {},
    selectedList = [],
    pageSelectCheckboxVariant = '',
    onHeaderSelect = () => {},
    isPageSelectCheckboxChecked = false,
    noListMessage = null,
    noSearchListMessage = null,
    searchResultCount,
    hideTopBar = false,
    hideBottomBar = false,
    isFilterActive = false,
    hideScrollBar = false,
    isLoading,
    loaderRows = 5,
    selectAllIdentifier
  } = props;
  const [showBottomBar, setShowBottomBar] = useState(false);
  const listItemsRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const sibListClass = classNames({
    [moduleStyles['sib-list']]: true,
    [className]: !!className
  });
  const sibListChildClass = classNames({
    [moduleStyles['sib-list__body']]: true,
    [moduleStyles['sib-list__body-noscroller']]: hideScrollBar
  });
  useEffect(() => {
    return () => {
      cancelDebounce();
    };
  }, []);
  useEffect(() => {
    if (!hideBottomBar) {
      listItemsRef?.current?.addEventListener('scroll', debouncedScroll);
      return () => {
        listItemsRef?.current?.removeEventListener('scroll', debouncedScroll);
      };
    }
  }, [listItemsRef?.current, lastScrollTopRef?.current, showBottomBar]);
  const scrollHandler = () => {
    const scrollHeight = listItemsRef?.current?.scrollHeight; // height of whole content
    const offsetHeight = listItemsRef?.current?.offsetHeight; // height of visible content
    const scrollTop = listItemsRef?.current?.scrollTop; // distance scrolled from top

    if (scrollTop < lastScrollTopRef?.current - 200) {
      // while scrolling up, we do not want to perform any calculations and simply remove the bottom bar if present.
      if (showBottomBar) {
        setShowBottomBar(false);
      }
      lastScrollTopRef.current = scrollTop <= 0 ? 0 : scrollTop; // updating last scrolled distance scrolled from top
      return;
    }
    lastScrollTopRef.current = scrollTop <= 0 ? 0 : scrollTop;
    const isEndOfList = scrollTop + offsetHeight >= scrollHeight - 300; // 300 px are reduced so that the BottomBar is added a little ahead of reaching the end of the list so that we do not see a flick or jerk in UI.
    if (isEndOfList) {
      setShowBottomBar(true);
    }
  };
  const {
    execDebounce: debouncedScroll,
    cancelDebounce
  } = useCallback(debounce(scrollHandler, 25), [listItemsRef?.current, lastScrollTopRef?.current, showBottomBar]);
  const {
    searchValue = ''
  } = listSearchInputProps;
  const isNoListFound = !children?.props?.children?.length && !searchValue && !isFilterActive;
  const isNoSearchResult = !children?.props?.children?.length && (searchValue || isFilterActive);
  return /*#__PURE__*/React.createElement(ListProvider, {
    listSelectionType: listSelectionType,
    listSearchInputProps: listSearchInputProps,
    bulkActionMetaData: bulkActionMetaData,
    selectedList: selectedList,
    noSearchListMessage: noSearchListMessage,
    isNoSearchResult: isNoSearchResult,
    noListMessage: noListMessage
  }, isNoListFound && !isLoading ? /*#__PURE__*/React.createElement(ErrorMessage, null) : /*#__PURE__*/React.createElement("div", {
    className: sibListClass
  }, /*#__PURE__*/React.createElement("div", {
    className: sibListChildClass,
    ref: listItemsRef
  }, !hideTopBar && /*#__PURE__*/React.createElement(ListTopBar, {
    paginationMetaData: topBarPaginationMetaData || paginationMetaData,
    pageSelectCheckboxVariant: pageSelectCheckboxVariant,
    onHeaderSelect: onHeaderSelect,
    isPageSelectCheckboxChecked: isPageSelectCheckboxChecked,
    topBarLeftSlot: topBarLeftSlot,
    topBarRightSlot: topBarRightSlot,
    searchResultCount: searchResultCount,
    isLoading: isLoading,
    selectAllIdentifier: selectAllIdentifier
  }), isLoading ? /*#__PURE__*/React.createElement(ListSkeleton, {
    listSelectionType: listSelectionType,
    skeletonRows: loaderRows,
    titleBlockProps: children.props?.children?.[0]?.props?.children?.props?.children?.[0]?.props
  }) : React.Children.map(children, (child, index) => {
    return /*#__PURE__*/React.cloneElement(child, {
      key: index,
      index
    });
  }), showBottomBar && !isLoading && /*#__PURE__*/React.createElement(ListBottomBar, {
    paginationMetaData: paginationMetaData,
    bottomBarLeftSlot: bottomBarLeftSlot,
    showMargin: listSelectionType === ListSelectionTypes.MULTI_SELECTION && selectedList.length > 0
  }))));
}
List.propTypes = {
  listSelectionType: PropTypes.string,
  children: PropTypes.node.isRequired,
  hideTopBar: PropTypes.bool,
  hideBottomBar: PropTypes.bool,
  isFilterActive: PropTypes.bool,
  hideScrollBar: PropTypes.bool,
  isLoading: PropTypes.bool,
  isPageSelectCheckboxChecked: PropTypes.bool,
  topBarLeftSlot: PropTypes.node,
  topBarRightSlot: PropTypes.node,
  bottomBarLeftSlot: PropTypes.node,
  listSearchInputProps: PropTypes.object,
  searchResultCount: PropTypes.number,
  selectAllIdentifier: PropTypes.string,
  paginationMetaData: PropTypes.object,
  topBarPaginationMetaData: PropTypes.object,
  bulkActionMetaData: PropTypes.object,
  onHeaderSelect: PropTypes.func,
  selectedList: PropTypes.array,
  noListMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    text: PropTypes.string.isRequired,
    subtext: PropTypes.string,
    actionButtonProps: PropTypes.object
  })]),
  noSearchListMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    text: PropTypes.string.isRequired,
    subtext: PropTypes.string,
    actionButtonProps: PropTypes.object
  })]),
  pageSelectCheckboxVariant: PropTypes.string,
  loaderRows: PropTypes.number,
  className: PropTypes.string
};
List.ItemGroup = ListItemGroup;
List.Item = ListItem;
List.DataCard = ListDataCard;
List.TitleBlock = ListTitleBlock;
List.MidSection = ListMidSection;
List.Actions = ListActions;
List.MoreActions = ListMoreActions;
export default List;